/**
 * Native push notification utilities for Capacitor mobile apps.
 * Handles FCM registration and notification handling for iOS and Android.
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { authenticatedFetch } from './nativeAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Check if native push notifications are supported
 */
export function isNativePushSupported() {
  return Capacitor.isNativePlatform();
}

/**
 * Initialize push notifications for native platforms.
 * Call this after user is authenticated.
 */
export async function initNativePushNotifications() {
  if (!isNativePushSupported()) {
    console.log('[NativePush] Not a native platform, skipping');
    return false;
  }

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive !== 'granted') {
      console.log('[NativePush] Permission denied');
      return false;
    }

    // Register with APNs/FCM
    await PushNotifications.register();

    // Set up listeners
    setupPushListeners();

    console.log('[NativePush] Initialized successfully');
    return true;
  } catch (error) {
    console.error('[NativePush] Initialization failed:', error);
    return false;
  }
}

/**
 * Set up push notification event listeners
 */
function setupPushListeners() {
  // Registration success - send token to backend
  PushNotifications.addListener('registration', async (token) => {
    console.log('[NativePush] Registration token:', token.value);
    await sendTokenToBackend(token.value);
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[NativePush] Registration error:', error);
  });

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[NativePush] Notification received:', notification);
    // You can show an in-app notification here if desired
  });

  // User tapped on notification
  PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('[NativePush] Notification action:', notification);
    handleNotificationTap(notification);
  });
}

/**
 * Send FCM token to backend for storage
 */
async function sendTokenToBackend(token) {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        platform: Capacitor.getPlatform(), // 'ios' or 'android'
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to register push token');
    }

    console.log('[NativePush] Token registered with backend');
  } catch (error) {
    console.error('[NativePush] Failed to send token to backend:', error);
  }
}

/**
 * Handle notification tap - navigate to relevant screen
 */
function handleNotificationTap(notification) {
  const data = notification.notification?.data;
  
  if (data?.url) {
    // Navigate to the URL specified in the notification
    // This will be handled by the app's router
    window.location.href = data.url;
  } else {
    // Default: go to expenses/history page
    window.location.href = '/history';
  }
}

/**
 * Check current push notification permission status
 */
export async function checkPushPermission() {
  if (!isNativePushSupported()) {
    return 'unsupported';
  }

  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive; // 'granted', 'denied', or 'prompt'
  } catch (error) {
    console.error('[NativePush] Permission check failed:', error);
    return 'unknown';
  }
}

/**
 * Remove all delivered notifications
 */
export async function clearAllNotifications() {
  if (!isNativePushSupported()) {
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('[NativePush] Failed to clear notifications:', error);
  }
}
